import { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  downloadSupportAttachment,
  fetchSupportAttachmentObjectUrl,
} from "../api/supportApi";
import {
  useAddSupportNote,
  useSupportReportQuery,
  useUpdateSupportReport,
} from "../hooks/useSupport";
import type { SupportReportStatus } from "../types";
import { statusBadge } from "./SupportReportsPage";

const STATUSES: SupportReportStatus[] = [
  "new",
  "open",
  "pending",
  "resolved",
  "closed",
];

interface Props {
  reportId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Inline thumbnail for a screenshot attachment. Renders the blob only through an
 * `<img>` (never as a top-level document), so even if a report stored a
 * malicious `image/svg+xml` / `text/html` payload it cannot execute script in
 * the backoffice origin — an `<img>` renders SVG statically and shows a broken
 * image for non-images. Click toggles an enlarged inline view (no new tab).
 */
const ScreenshotAttachment = ({
  reportId,
  attId,
  name,
}: {
  reportId: string;
  attId: string;
  name: string;
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    fetchSupportAttachmentObjectUrl(reportId, attId)
      .then((u) => {
        if (alive) {
          objectUrl = u;
          setUrl(u);
        } else {
          URL.revokeObjectURL(u);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [reportId, attId]);

  if (!url) {
    return (
      <div className="flex h-32 w-32 items-center justify-center rounded-lg border border-neutral-700 bg-neutral-800/30 text-xs text-neutral-500">
        …
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      aria-expanded={expanded}
      aria-label={
        expanded ? `Achicar captura ${name}` : `Agrandar captura ${name}`
      }
      title={
        expanded
          ? `${name} — clic para achicar`
          : `${name} — clic para agrandar`
      }
      className="cursor-zoom-in overflow-hidden rounded-lg border border-neutral-700 transition hover:opacity-90"
    >
      <img
        src={url}
        alt={name}
        className={
          expanded
            ? "max-h-[70vh] max-w-full object-contain"
            : "h-32 w-32 object-cover"
        }
      />
    </button>
  );
};

const SupportReportDetail = ({ reportId, open, onOpenChange }: Props) => {
  const { t } = useTranslation("common");
  const { data: report, isLoading } = useSupportReportQuery(reportId);
  const updateMutation = useUpdateSupportReport();
  const addNoteMutation = useAddSupportNote();

  const [status, setStatus] = useState<SupportReportStatus>("new");
  const [severity, setSeverity] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [noteText, setNoteText] = useState("");

  useEffect(() => {
    if (report) {
      setStatus(report.status);
      setSeverity(report.severity ?? "");
      setAssignedTo(report.assignedTo ?? "");
      setNoteText("");
    }
  }, [report]);

  const handleSave = async () => {
    if (!report) return;
    await updateMutation.mutateAsync({
      id: report.id,
      patch: {
        status,
        severity: severity || undefined,
        assignedTo: assignedTo || undefined,
      },
    });
  };

  const handleAddNote = async () => {
    if (!report || !noteText.trim()) return;
    await addNoteMutation.mutateAsync({ id: report.id, text: noteText.trim() });
    setNoteText("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-neutral-800 bg-neutral-900 text-neutral-100">
        {isLoading || !report ? (
          <div className="py-10 text-center text-sm text-neutral-400">
            {t("SUPPORT.loading")}
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <span>{t("SUPPORT.reportTitle")}</span>
                {statusBadge(report.status)}
              </DialogTitle>
              <DialogDescription className="text-neutral-400">
                {report.userSnapshot?.email ||
                  report.userSnapshot?.username ||
                  (report.userId
                    ? `#${report.userId}`
                    : t("SUPPORT.anonymous"))}{" "}
                &middot; {report.source}
                {report.category ? ` · ${report.category}` : ""} &middot;{" "}
                {formatDistanceToNow(new Date(report.createdAt), {
                  addSuffix: true,
                  locale: es,
                })}
              </DialogDescription>
            </DialogHeader>

            {/* Message */}
            <div className="rounded-lg border border-neutral-700 bg-neutral-800/50 p-4">
              <p className="whitespace-pre-wrap leading-relaxed text-neutral-200">
                {report.message}
              </p>
            </div>

            {/* Environment */}
            <div className="grid grid-cols-2 gap-2 text-xs text-neutral-400 sm:grid-cols-4">
              <div>
                <span className="text-neutral-500">v</span>{" "}
                {report.appVersion || "—"}
              </div>
              <div>
                <span className="text-neutral-500">SO</span>{" "}
                {report.platform || "—"} {report.osVersion || ""}
              </div>
              <div>
                <span className="text-neutral-500">arch</span>{" "}
                {report.arch || "—"}
              </div>
              <div>
                <span className="text-neutral-500">id</span>{" "}
                <span className="font-mono">{report.id.slice(0, 8)}</span>
              </div>
            </div>

            {/* Attachments */}
            {report.attachments.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="text-sm font-semibold text-neutral-200">
                  {t("SUPPORT.attachments")}
                </p>
                {report.attachments.some((a) => a.kind === "screenshot") && (
                  <div className="flex flex-wrap gap-2">
                    {report.attachments
                      .filter((a) => a.kind === "screenshot")
                      .map((a) => (
                        <ScreenshotAttachment
                          key={a.id}
                          reportId={report.id}
                          attId={a.id}
                          name={a.name}
                        />
                      ))}
                  </div>
                )}
                {/* Todos los attachments (incluidos los screenshots, que
                    además se ven inline arriba) quedan descargables. */}
                <div className="flex flex-wrap gap-2">
                  {report.attachments.map((a) => (
                    <Button
                      key={a.id}
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        downloadSupportAttachment(report.id, a.id, a.name)
                      }
                    >
                      ⬇ {a.name} ({Math.max(1, Math.round(a.sizeBytes / 1024))}{" "}
                      KB)
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Diagnostics */}
            {report.diagnostics && (
              <details className="rounded-lg border border-neutral-700 bg-neutral-800/30">
                <summary className="cursor-pointer px-3 py-2 text-sm text-neutral-300">
                  {t("SUPPORT.diagnostics")}
                </summary>
                <pre className="max-h-64 overflow-auto px-3 pb-3 text-[11px] leading-snug text-neutral-400">
                  {JSON.stringify(report.diagnostics, null, 2)}
                </pre>
              </details>
            )}

            {/* Triage controls */}
            <div className="flex flex-wrap items-end gap-3 rounded-lg border border-neutral-800 bg-neutral-800/30 p-3">
              <label className="flex flex-col gap-1 text-xs text-neutral-400">
                {t("SUPPORT.colStatus")}
                <select
                  value={status}
                  onChange={(e) =>
                    setStatus(e.target.value as SupportReportStatus)
                  }
                  className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-sm text-neutral-200"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs text-neutral-400">
                {t("SUPPORT.severity")}
                <input
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                  placeholder="—"
                  className="w-28 rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-sm text-neutral-200"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-neutral-400">
                {t("SUPPORT.assignedTo")}
                <input
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  placeholder="—"
                  className="w-40 rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-sm text-neutral-200"
                />
              </label>
              <Button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="bg-purple-600 text-white hover:bg-purple-700"
              >
                {t("SUPPORT.save")}
              </Button>
            </div>

            {/* Internal notes */}
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-neutral-200">
                {t("SUPPORT.internalNotes")}
              </p>
              {report.internalNotes.length === 0 ? (
                <p className="text-xs text-neutral-500">
                  {t("SUPPORT.noNotes")}
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {report.internalNotes.map((n, i) => (
                    <li
                      key={i}
                      className="rounded-md border border-neutral-800 bg-neutral-800/30 p-2 text-sm text-neutral-300"
                    >
                      <span className="whitespace-pre-wrap">{n.text}</span>
                      <span className="mt-1 block text-[11px] text-neutral-500">
                        {n.authorId} ·{" "}
                        {formatDistanceToNow(new Date(n.at), {
                          addSuffix: true,
                          locale: es,
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex gap-2">
                <input
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder={t("SUPPORT.notePlaceholder")}
                  className="flex-1 rounded-md border border-neutral-700 bg-neutral-900 px-3 py-1.5 text-sm text-neutral-200"
                />
                <Button
                  variant="outline"
                  onClick={handleAddNote}
                  disabled={addNoteMutation.isPending || !noteText.trim()}
                >
                  {t("SUPPORT.addNote")}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SupportReportDetail;
